/**
 * Supprime les appels d'outils que le modèle a écrits EN PROSE au lieu de les
 * émettre sur le canal `tool_calls` natif.
 *
 * WHY (incident 2026-09-01, mesuré en base sur `casediscovery_messages_v2`) :
 * DeepSeek V4 Flash dérive en fin de longue conversation et rédige ses appels
 * d'outils dans le canal texte, en grammaire Anthropic :
 *
 *     <invoke name="replace_text">
 *     <parameter name="search_text">…</parameter>
 *     </invoke>
 *
 * Ce n'est ni le format natif DeepSeek ni le JSON OpenAI que parle l'endpoint :
 * il n'y a donc AUCUN appel à parser côté transport — le modèle écrit de la prose
 * qui imite un appel d'outil. Conséquence pour l'avocat : rien ne s'exécute, le
 * document Word n'est pas modifié, et du XML brut s'affiche dans la bulle.
 *
 * Mesure au 2026-09-01 : 175 messages assistants, 22 conversations, 20 avocats,
 * quasi exclusivement `hector_v3` (add-in Word) sur `deepseek-v4-flash`.
 *
 * La profondeur de conversation CORRÈLE sans expliquer : premier tour fuité par
 * fil = médiane 102, quartiles 58–248, mais minimum 10 et 5 fils sur 22 fuient
 * avant le tour 50. Ce n'est donc PAS un pur effet de long contexte — raccourcir
 * les conversations réduirait l'exposition, sans supprimer le phénomène.
 *
 * Une fois amorcée, la fuite se répète dans le même fil (jusqu'à 62 fois) — d'où
 * le nettoyage AVANT persistance côté backend : laisser le bloc dans l'historique
 * réapprend le motif au modèle au tour suivant.
 *
 * Familles couvertes (toutes observées en base) :
 *  - `<invoke name="…">…</invoke>` complet (151 messages)
 *  - le même bloc NON refermé, coupé en fin de génération (24 messages)
 *  - `<parameter name="…">…</parameter>` orphelin, opener `<invoke` perdu (6)
 *  - marqueurs nus `<tool_calls>` / `<tool_call>` / `<function_calls>` (30)
 *
 * Hors périmètre volontaire : les pseudo-balises inventées (`<fix-block/>`,
 * `<correct-p8/>`) — même dérive, mais les strip toutes reviendrait à casser la
 * politique « tag inconnu laissé intact » du normalizer, qui protège la prose
 * juridique. Seules les formes STRUCTURELLES d'appel d'outil sont retirées.
 */

export interface ToolCallLeak {
  /** Nom de l'outil tel qu'écrit par le modèle (`name="…"`), null si illisible. */
  name: string | null;
}

export interface ToolCallLeakResult {
  /** Texte débarrassé des blocs d'appel d'outil. */
  text: string;
  /** Un élément par bloc retiré. Vide = pas de fuite. */
  leaks: ToolCallLeak[];
}

/** Balises structurelles d'appel d'outil, tous dialectes confondus. */
const TOOL_CALL_TAGS = '(?:antml:)?(?:function_calls|tool_calls|tool_call|invoke|parameter)';

/**
 * Fin de nom de balise : espace, `/`, `>` ou fin de chaîne (flux coupé).
 *
 * WHY pas `\b` : `\b` matche AUSSI avant un tiret, si bien que `<invoke-find-residual/>`
 * — une pseudo-balise inventée par la même dérive, mais hors périmètre — passait pour
 * un `<invoke>` non refermé et faisait tronquer tout le reste du message. Mesuré sur
 * le message `ac05af1d…` : la phrase « Je poursuis le contrôle des intrants résiduels »
 * était emportée. Le nom doit se terminer ICI, pas se continuer.
 */
const TAG_NAME_END = '(?=[\\s/>]|$)';

const INVOKE_BLOCK_RE = new RegExp(`<(?:antml:)?invoke${TAG_NAME_END}[^>]*>[\\s\\S]*?</(?:antml:)?invoke\\s*>`, 'gi');
const INVOKE_OPENER_RE = new RegExp(`<(?:antml:)?invoke${TAG_NAME_END}[^>]*>?`, 'i');
const PARAMETER_BLOCK_RE = new RegExp(`<(?:antml:)?parameter${TAG_NAME_END}[^>]*>[\\s\\S]*?</(?:antml:)?parameter\\s*>`, 'gi');
const PARAMETER_OPENER_RE = new RegExp(`<(?:antml:)?parameter${TAG_NAME_END}[^>]*>?`, 'i');
const BARE_TAG_RE = new RegExp(`</?${TOOL_CALL_TAGS}${TAG_NAME_END}[^>]*/?>`, 'gi');
const NAME_ATTR_RE = /\bname\s*=\s*"([^"]*)"/i;

/**
 * Marqueur posé à la place d'un bloc retiré, le temps de savoir si la ligne qui
 * le portait ne contenait QUE ça (auquel cas la ligne entière disparaît).
 *
 * Zone privée Unicode, jamais NUL : Postgres refuse 0x00 et ce texte finit en
 * base via `MessageRepository.saveMessage`.
 */
const CUT = '\uE000';

/** Extrait `name="…"` d'un opener `<invoke …>`. */
function readToolName(block: string): string | null {
  const match = NAME_ATTR_RE.exec(block);
  return match?.[1] ?? null;
}

/**
 * Retire les appels d'outils écrits en texte d'un segment de PROSE.
 *
 * Ne pas appeler sur du code : utiliser `stripTextEmittedToolCalls` (fence-safe)
 * ou la pipeline `normalizeAssistantText`, qui isolent les fences au préalable.
 */
export function toolCallLeak(text: string): ToolCallLeakResult {
  if (!text || text.indexOf('<') === -1) return { text, leaks: [] };

  const leaks: ToolCallLeak[] = [];
  let out = text;

  // 1. Blocs <invoke>…</invoke> complets.
  out = out.replace(INVOKE_BLOCK_RE, (block) => {
    leaks.push({ name: readToolName(block) });
    return CUT;
  });

  // 2. Bloc <invoke …> non refermé : la génération a été coupée au milieu de
  //    l'appel. Tout ce qui suit l'opener EST le corps de l'appel — on tronque
  //    jusqu'à la fin plutôt que de laisser les arguments traîner en prose.
  const invokeOpener = INVOKE_OPENER_RE.exec(out);
  if (invokeOpener) {
    leaks.push({ name: readToolName(out.slice(invokeOpener.index)) });
    out = out.slice(0, invokeOpener.index);
  }

  // 3. <parameter>…</parameter> orphelins (opener <invoke> jamais émis).
  //    `name` reste null : l'attribut porte ici un nom d'ARGUMENT, pas d'outil —
  //    le remonter dans `leakedToolNames` induirait en erreur au débogage.
  out = out.replace(PARAMETER_BLOCK_RE, () => {
    leaks.push({ name: null });
    return CUT;
  });

  const parameterOpener = PARAMETER_OPENER_RE.exec(out);
  if (parameterOpener) {
    leaks.push({ name: null });
    out = out.slice(0, parameterOpener.index);
  }

  // 4. Marqueurs nus restants (<tool_calls>, </function_calls>, …). Contrairement
  //    aux blocs ci-dessus ils n'encadrent rien d'utile : la prose autour est
  //    légitime, on retire le marqueur sur place.
  out = out.replace(BARE_TAG_RE, () => {
    leaks.push({ name: null });
    return CUT;
  });

  if (leaks.length === 0) return { text, leaks };

  // Le retrait laisse des trous : une ligne qui ne portait QUE des blocs retirés
  // disparaît entièrement (sinon l'avocat voit un blanc là où était le XML) ; un
  // marqueur au milieu d'une phrase s'efface sur place.
  out = out
    .split('\n')
    .filter((line) => !(line.includes(CUT) && line.split(CUT).join('').trim() === ''))
    .join('\n')
    .split(CUT)
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();

  return { text: out, leaks };
}
